// ============================================
// PAGE - Registro de Caja
// ============================================

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { MdArrowBack, MdHistory, MdClose, MdSearch, MdLock } from 'react-icons/md';
import { Button } from '@almacenes/ui';
import { useCuadraturasAbiertas, useCuadraturasConFiltros, useCerrarCajaById, useUIStore, formatFechaLocal, type CuadraturaCaja } from '@almacenes/core';

type EstadoFiltro = 'OPEN' | 'CLOSED' | 'NONE';

export const RegistroCajaPage = () => {
  const navigate = useNavigate();
  const addToast = useUIStore((state) => state.addToast);
  const cerrarCajaMutation = useCerrarCajaById();

  // Estados
  const [estadoFiltro, setEstadoFiltro] = useState<EstadoFiltro>('OPEN');
  const [fechaInicio, setFechaInicio] = useState('');
  const [fechaFin, setFechaFin] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [cajaSeleccionada, setCajaSeleccionada] = useState<CuadraturaCaja | null>(null);
  const [showDetalleModal, setShowDetalleModal] = useState(false);
  const [showCerrarModal, setShowCerrarModal] = useState(false);

  // Queries
  const { data: cuadraturasAbiertas, isLoading: isLoadingAbiertas, refetch: refetchAbiertas } = useCuadraturasAbiertas();
  
  const shouldFetchConFiltros = estadoFiltro !== 'OPEN' && showFilters && !!fechaInicio && !!fechaFin;
  
  const { data: cuadraturasConFiltros, isLoading: isLoadingFiltros, refetch: refetchFiltros } = useCuadraturasConFiltros(
    {
      fechaInicio: convertirFecha(fechaInicio), // YYYY-MM-DD a DD-MM-YYYY
      fechaFin: convertirFecha(fechaFin),
    },
    shouldFetchConFiltros
  );

  // Determinar qué datos mostrar
  const cuadraturas = estadoFiltro === 'OPEN' 
    ? cuadraturasAbiertas?.content || []
    : cuadraturasConFiltros?.content || [];
  
  const isLoading = estadoFiltro === 'OPEN' ? isLoadingAbiertas : isLoadingFiltros;

  // Función para convertir fecha de YYYY-MM-DD a DD-MM-YYYY
  function convertirFecha(fecha: string): string {
    if (!fecha) return '';
    const [year, month, day] = fecha.split('-');
    return `${day}-${month}-${year}`;
  }

  // Formatear número con separador de miles
  const formatearMonto = (valor: number | null): string => {
    if (valor === null) return '-';
    return `$ ${valor.toLocaleString('es-CL')}`;
  };

  // Calcular recaudación total en efectivo
  const calcularRecaudacionEfectivo = (caja: CuadraturaCaja): number | null => {
    if (caja.estado === 'OPEN') return null;
    
    const ventas = (caja.ventasEfectivo || 0) +
                   (caja.ventasDebito || 0) +
                   (caja.ventasCredito || 0) +
                   (caja.ventasFiado || 0) +
                   (caja.ventasTransferencia || 0) +
                   (caja.otrosIngresos || 0);
    
    return ventas;
  };

  // Manejar cierre de caja
  const handleCerrarCaja = async () => {
    if (!cajaSeleccionada) return;

    try {
      await cerrarCajaMutation.mutateAsync(cajaSeleccionada.id);
      
      addToast('success', 'Caja cerrada exitosamente');
      setShowCerrarModal(false);
      setCajaSeleccionada(null);
      
      // Refrescar datos
      if (estadoFiltro === 'OPEN') {
        refetchAbiertas();
      } else {
        refetchFiltros();
      }
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || error?.message || 'Error al cerrar la caja';
      addToast('error', errorMessage);
    }
  };

  // Manejar búsqueda con filtros
  const handleBuscar = () => {
    if (estadoFiltro === 'OPEN') {
      refetchAbiertas();
      setShowFilters(false);
    } else {
      if (!fechaInicio || !fechaFin) {
        addToast('error', 'Debes seleccionar un rango de fechas');
        return;
      }
      setShowFilters(true);
    }
  };

  // Inicializar fechas por defecto para filtros
  useEffect(() => {
    if (estadoFiltro !== 'OPEN') {
      const hoy = new Date();
      const haceUnMes = new Date();
      haceUnMes.setMonth(haceUnMes.getMonth() - 1);
      
      setFechaFin(formatFechaLocal(hoy));
      setFechaInicio(formatFechaLocal(haceUnMes));
    }
  }, [estadoFiltro]);

  return (
    <div className="px-4 md:px-6 lg:px-8 pb-8">
      {/* Header */}
      <div className="mb-6 flex items-center gap-4">
        <button
          onClick={() => navigate('/caja-360')}
          className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
        >
          <MdArrowBack className="text-2xl" style={{ color: '#43BFFF' }} />
        </button>
        <div>
          <h1 className="text-3xl font-bold" style={{ color: '#11194F' }}>
            Registro de Caja
          </h1>
          <p className="text-gray-600 mt-1">
            Historial de movimientos y cuadraturas
          </p>
        </div>
      </div>

      {/* Filtros */}
      <div className="bg-white rounded-xl border border-gray-200 p-4 md:p-6 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {/* Estado */}
          <div>
            <label className="block text-sm font-medium mb-2" style={{ color: '#11194F' }}>
              Estado
            </label>
            <select
              value={estadoFiltro}
              onChange={(e) => setEstadoFiltro(e.target.value as EstadoFiltro)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-200"
            >
              <option value="OPEN">Abierto</option>
              <option value="CLOSED">Cerrado</option>
              <option value="NONE">Todos</option>
            </select>
          </div>

          {/* Fecha Desde */}
          <div>
            <label className="block text-sm font-medium mb-2" style={{ color: '#11194F' }}>
              Fecha desde: {estadoFiltro !== 'OPEN' && <span className="text-red-500">*</span>}
            </label>
            <input
              type="date"
              value={fechaInicio}
              onChange={(e) => setFechaInicio(e.target.value)}
              disabled={estadoFiltro === 'OPEN'}
              className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-200 ${
                estadoFiltro === 'OPEN' ? 'bg-gray-100 cursor-not-allowed' : ''
              }`}
            />
          </div>

          {/* Fecha Hasta */}
          <div>
            <label className="block text-sm font-medium mb-2" style={{ color: '#11194F' }}>
              Fecha hasta: {estadoFiltro !== 'OPEN' && <span className="text-red-500">*</span>}
            </label>
            <input
              type="date"
              value={fechaFin}
              onChange={(e) => setFechaFin(e.target.value)}
              disabled={estadoFiltro === 'OPEN'}
              className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-200 ${
                estadoFiltro === 'OPEN' ? 'bg-gray-100 cursor-not-allowed' : ''
              }`}
            />
          </div>

          {/* Botón Buscar */}
          <div className="flex items-end">
            <Button
              variant="primary"
              onClick={handleBuscar}
              className="w-full"
            >
              <MdSearch className="inline-block mr-2 text-xl" />
              Buscar
            </Button>
          </div>
        </div>
      </div>

      {/* Título de la tabla */}
      <h2 className="text-2xl font-bold mb-4" style={{ color: '#11194F' }}>
        Registro de cajas
      </h2>

      {/* Tabla */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        {isLoading ? (
          <div className="p-12 text-center">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
            <p className="text-gray-600 mt-4">Cargando registros...</p>
          </div>
        ) : cuadraturas.length === 0 ? (
          <div className="p-12 text-center">
            <MdHistory className="text-6xl text-gray-300 mx-auto mb-4" />
            <p className="text-gray-600">No se encontraron registros</p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-semibold" style={{ color: '#11194F' }}>Cajero</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold" style={{ color: '#11194F' }}>Fecha Registro</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold" style={{ color: '#11194F' }}>Fecha Apertura</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold" style={{ color: '#11194F' }}>Fecha Cierre</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold" style={{ color: '#11194F' }}>Monto Inicial</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold" style={{ color: '#11194F' }}>
                      Ventas Efectivo <span className="text-red-500">*</span>
                    </th>
                    <th className="px-4 py-3 text-left text-sm font-semibold" style={{ color: '#11194F' }}>
                      Recaudación en Efectivo <span className="text-red-500">*</span>
                    </th>
                    <th className="px-4 py-3 text-left text-sm font-semibold" style={{ color: '#11194F' }}>Estado</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold" style={{ color: '#11194F' }}>Acciones</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {cuadraturas.map((caja) => (
                    <tr key={caja.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm font-semibold" style={{ color: '#11194F' }}>{caja.cajero}</td>
                      <td className="px-4 py-3 text-sm text-gray-600">{caja.fechaRegistro}</td>
                      <td className="px-4 py-3 text-sm text-gray-600">
                        {caja.fechaInicio} {caja.horaInicio}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">
                        {caja.fechaCierre ? `${caja.fechaCierre} ${caja.horaCierre}` : '-'}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">{formatearMonto(caja.montoInicial)}</td>
                      <td className="px-4 py-3 text-sm text-gray-600">{formatearMonto(caja.ventasEfectivo)}</td>
                      <td className="px-4 py-3 text-sm text-gray-600">{formatearMonto(calcularRecaudacionEfectivo(caja))}</td>
                      <td className="px-4 py-3">
                        <span
                          className={`inline-block px-3 py-1 rounded-full text-xs font-semibold ${
                            caja.estado === 'OPEN'
                              ? 'bg-green-100 text-green-800'
                              : 'bg-red-100 text-red-800'
                          }`}
                        >
                          {caja.estado === 'OPEN' ? 'Abierta' : 'Cerrado'}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        {caja.estado === 'OPEN' ? (
                          <button
                            onClick={() => {
                              setCajaSeleccionada(caja);
                              setShowCerrarModal(true);
                            }}
                            className="p-2 rounded-lg bg-red-500 hover:bg-red-600 text-white transition-colors"
                            title="Cerrar caja"
                          >
                            <MdLock className="text-xl" />
                          </button>
                        ) : (
                          <button
                            onClick={() => {
                              setCajaSeleccionada(caja);
                              setShowDetalleModal(true);
                            }}
                            className="p-2 rounded-lg bg-blue-500 hover:bg-blue-600 text-white transition-colors"
                            title="Ver detalle"
                          >
                            <MdSearch className="text-xl" />
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            {/* Nota al pie */}
            <div className="px-4 py-3 bg-gray-50 border-t border-gray-200">
              <p className="text-xs text-gray-600 text-center">
                Los valores marcados con <span className="text-red-500">*</span> aplican solo cuando la caja está cerrada.
              </p>
            </div>
          </>
        )}
      </div>

      {/* Modal Cerrar Caja */}
      {showCerrarModal && cajaSeleccionada && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={() => setShowCerrarModal(false)}
        >
          <div 
            className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-2xl font-bold mb-4" style={{ color: '#11194F' }}>
              Confirmar Cierre de Caja
            </h3>
            
            <p className="text-gray-600 mb-6">
              ¿Estás seguro de que deseas cerrar la caja de <strong>{cajaSeleccionada.cajero}</strong>?
            </p>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
              <p className="text-sm text-blue-800">
                <strong>Nota:</strong> Una vez cerrada, se calcularán automáticamente todas las ventas del día y no podrás reabrir esta caja.
              </p>
            </div>

            <div className="flex gap-3">
              <Button
                variant="secondary"
                onClick={() => setShowCerrarModal(false)}
                disabled={cerrarCajaMutation.isPending}
                className="flex-1"
              >
                Cancelar
              </Button>
              <Button
                variant="danger"
                onClick={handleCerrarCaja}
                isLoading={cerrarCajaMutation.isPending}
                disabled={cerrarCajaMutation.isPending}
                className="flex-1"
              >
                Sí, Cerrar Caja
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Detalle de Caja */}
      {showDetalleModal && cajaSeleccionada && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={() => setShowDetalleModal(false)}
        >
          <div 
            className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-start mb-6">
              <h3 className="text-2xl font-bold" style={{ color: '#11194F' }}>
                Detalle de Caja
              </h3>
              <button
                onClick={() => setShowDetalleModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <MdClose className="text-2xl" />
              </button>
            </div>

            <div className="space-y-4">
              {/* Información General */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="font-semibold mb-3" style={{ color: '#11194F' }}>Información General</h4>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <p className="text-sm text-gray-600">Cajero</p>
                    <p className="font-semibold" style={{ color: '#11194F' }}>{cajaSeleccionada.cajero}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Estado</p>
                    <span
                      className={`inline-block px-3 py-1 rounded-full text-xs font-semibold ${
                        cajaSeleccionada.estado === 'OPEN'
                          ? 'bg-green-100 text-green-800'
                          : 'bg-red-100 text-red-800'
                      }`}
                    >
                      {cajaSeleccionada.estado === 'OPEN' ? 'Abierta' : 'Cerrado'}
                    </span>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Fecha de Apertura</p>
                    <p className="font-semibold" style={{ color: '#11194F' }}>
                      {cajaSeleccionada.fechaInicio} {cajaSeleccionada.horaInicio}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Fecha de Cierre</p>
                    <p className="font-semibold" style={{ color: '#11194F' }}>
                      {cajaSeleccionada.fechaCierre ? `${cajaSeleccionada.fechaCierre} ${cajaSeleccionada.horaCierre}` : '-'}
                    </p>
                  </div>
                </div>
              </div>

              {/* Montos */}
              <div className="bg-blue-50 rounded-lg p-4">
                <h4 className="font-semibold mb-3" style={{ color: '#11194F' }}>Montos</h4>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Monto Inicial:</span>
                    <span className="font-semibold" style={{ color: '#11194F' }}>{formatearMonto(cajaSeleccionada.montoInicial)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Ventas en Efectivo:</span>
                    <span className="font-semibold" style={{ color: '#11194F' }}>{formatearMonto(cajaSeleccionada.ventasEfectivo)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Ventas en Débito:</span>
                    <span className="font-semibold" style={{ color: '#11194F' }}>{formatearMonto(cajaSeleccionada.ventasDebito)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Ventas en Crédito:</span>
                    <span className="font-semibold" style={{ color: '#11194F' }}>{formatearMonto(cajaSeleccionada.ventasCredito)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Ventas al Fiado:</span>
                    <span className="font-semibold" style={{ color: '#11194F' }}>{formatearMonto(cajaSeleccionada.ventasFiado)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Ventas por Transferencia:</span>
                    <span className="font-semibold" style={{ color: '#11194F' }}>{formatearMonto(cajaSeleccionada.ventasTransferencia)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Otros Ingresos:</span>
                    <span className="font-semibold" style={{ color: '#11194F' }}>{formatearMonto(cajaSeleccionada.otrosIngresos)}</span>
                  </div>
                  <div className="border-t-2 border-gray-300 pt-2 mt-2">
                    <div className="flex justify-between">
                      <span className="font-bold" style={{ color: '#11194F' }}>Recaudación Total:</span>
                      <span className="font-bold text-lg" style={{ color: '#22C55E' }}>{formatearMonto(calcularRecaudacionEfectivo(cajaSeleccionada))}</span>
                    </div>
                  </div>
                </div>
              </div>

              <Button
                variant="secondary"
                onClick={() => setShowDetalleModal(false)}
                className="w-full"
              >
                Cerrar
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
